import { GetSurveyRuleKeyOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleKeyOptionsRPC";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import {
  applySnapshot,
  cast,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { SurveyRuleKeyOptionModel } from "../models/SurveyRuleKeyOptionModel";
import { getAPIClient } from "../../networking/APIClient";
import { useGetSurveyRuleKeyOptionsRPCClientImpl } from "../rpcs/RPC";

interface GetSurveyRuleKeyOptionsStoreInitialState {
  loadingGetSurveyRuleKeyOptions: Record<string, boolean>;
  keyOption: Record<string, SnapshotIn<typeof SurveyRuleKeyOptionModel>>;
  getSurveyRuleKeyOptionsRPCError: Record<
    string,
    GetSurveyRuleKeyOptionsRPC.RPCError
  >;
}

interface RespondentColumnNameLike {
  readonly name?: string;
}

const getInitialState = (): GetSurveyRuleKeyOptionsStoreInitialState => ({
  loadingGetSurveyRuleKeyOptions: {},
  keyOption: {},
  getSurveyRuleKeyOptionsRPCError: {},
});

const getRespondentColumnName = (name: unknown): string => {
  const respondentColumnName = name as RespondentColumnNameLike;
  if (typeof respondentColumnName.name === "string") {
    return respondentColumnName.name;
  }
  return String(name);
};

const mapToSurveyRuleKeyOptionModel = (
  keyOption: GetSurveyRuleKeyOptionsRPC.ResponseEnums.KeyOption.KeyOption,
): SnapshotIn<typeof SurveyRuleKeyOptionModel> => {
  if (
    keyOption instanceof
    GetSurveyRuleKeyOptionsRPC.ResponseEnums.KeyOption.QuestionCode
  ) {
    return {
      type: "QUESTION_CODE",
      questionCodes: keyOption.codes.map((code) => code.code),
    };
  }

  if (
    keyOption instanceof
    GetSurveyRuleKeyOptionsRPC.ResponseEnums.KeyOption.RespondentColumnNames
  ) {
    return {
      type: "RESPONDENT_COLUMN_NAME",
      respondentColumnNames: keyOption.names.map(getRespondentColumnName),
    };
  }

  return keyOption satisfies never;
};

export const GetSurveyRuleKeyOptionsStore = types
  .model("GetSurveyRuleKeyOptionsStore", {
    keyOption: types.map(SurveyRuleKeyOptionModel),
    loadingGetSurveyRuleKeyOptions: types.map(types.boolean),
    getSurveyRuleKeyOptionsRPCError: types.map(
      types.enumeration(
        "getSurveyRuleKeyOptionsRPCError",
        Object.values(GetSurveyRuleKeyOptionsRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getSurveyRuleKeyOptions: flow(function* (
      surveyId: string,
      keyType: GetSurveyRuleKeyOptionsRPC.RequestEnums.KeyType.KeyType,
      conditionId?: string,
    ) {
      const optionsKey = conditionId ?? "default";
      try {
        store.keyOption.delete(optionsKey);
        store.getSurveyRuleKeyOptionsRPCError.delete(optionsKey);
        store.loadingGetSurveyRuleKeyOptions.set(optionsKey, true);

        const apiClient = getAPIClient(store);
        const request = new GetSurveyRuleKeyOptionsRPC.Request(
          new LeoUUID(surveyId),
          keyType,
        );

        const result: LeoRPCResult<
          GetSurveyRuleKeyOptionsRPC.Response,
          GetSurveyRuleKeyOptionsRPC.Errors.Errors
        > =
          yield useGetSurveyRuleKeyOptionsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          const keyOption = mapToSurveyRuleKeyOptionModel(
            result.response.keyOption,
          );
          store.keyOption.set(optionsKey, cast(keyOption));
          return;
        }

        switch (result.error.code) {
          case GetSurveyRuleKeyOptionsRPC.RPCError.InvalidSurveyId:
            store.getSurveyRuleKeyOptionsRPCError.set(
              optionsKey,
              result.error.code,
            );
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingGetSurveyRuleKeyOptions.delete(optionsKey);
      }
    }),

    resetStore: (): void => {
      applySnapshot(store, getInitialState());
    },
  }));

export const createGetSurveyRuleKeyOptionsStore = (): Instance<
  typeof GetSurveyRuleKeyOptionsStore
> => GetSurveyRuleKeyOptionsStore.create(getInitialState());
